import * as React from "react";
import { ViewLarge, Viewport, ViewSmall } from "@shared-ui/viewport-context";
import { useLocalization } from "@shared-ui/localization-context";

import { UitkHeading } from "@egds/react-core/text";
import { UitkLayoutGrid, UitkLayoutGridItem } from "@egds/react-core/layout-grid";
import { AnalyticsConfig } from "@egds/react-core/utils";

import SimilarDestinationCard from "./components/SimilarDestinationCard";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

import { withStores } from "stores";
import { SimilarDestinationsProps } from "./typings";
import { TeSimilarDestinationFlexModuleResult } from "src/typings/microserviceModels/te-similar-destination-flex-module";
import { IsomorphicCarousel } from "src/components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { cropResizeSrc } from "src/components/shared/BlossomImage/BlossomImage";
import { UitkSpacing } from "@egds/react-core/spacing";

const SimilarDestinations = withStores(
  "analytics",
  "flexModuleModelStore"
)((props: SimilarDestinationsProps) => {
  const { templateComponent, flexModuleModelStore, context } = props;
  const {
    metadata: { id },
    config: { fmId, fmTitleId },
  } = templateComponent;
  const model = flexModuleModelStore.getModel(id) as TeSimilarDestinationFlexModuleResult | null;

  /* istanbul ignore next */
  if (!model) {
    return null;
  }

  const { content, title } = model;
  const { items: destinations } = content;
  const { formatText } = useLocalization();
  const localizedTitle = title || formatText("similarDestinations.title", context.searchContext.location.name);
  const destinationKeyHelper = new ItemKeyHelper("similar-destinations");

  // This is covered in e2e test
  /* istanbul ignore next */
  const analytics: AnalyticsConfig = {
    id: "TG.LP.Dest.OtherPlaces",
    callback: (moduleId, description) => {
      const flexTrackingInfo: FlexTrackingInfo = {
        moduleName: moduleId,
        action: Action.SCROLL,
        linkName: description,
      };
      sendDelayedTrackEvent(flexTrackingInfo, props.analytics);
    },
  };

  const optimizedImageUrl = (url: string) => {
    return cropResizeSrc(url, { width: 330, height: 190 }, "medium");
  };

  return (
    <Viewport>
      <ViewSmall>
        <UitkSpacing margin={{ blockend: "six" }}>
          <div
            id={id}
            className="SimilarDestinations SimilarDestinations--carousel"
            data-fm={fmId}
            data-fm-title-id={fmTitleId}
          >
            <UitkSpacing padding={{ inlinestart: "three" }} margin={{ blockend: "three" }}>
              <UitkHeading tag="h3" size={4}>
                {localizedTitle}
              </UitkHeading>
            </UitkSpacing>
            <UitkSpacing padding={{ inlinestart: "three" }}>
              <IsomorphicCarousel
                analytics={analytics}
                carouselName="SimilarDestinations"
                itemsVisible={{ lg: 1, md: 1, sm: 1 }}
                pageBy={1}
                peek
                buttonText={{
                  nextButton: formatText("carousel.item.next"),
                  prevButton: formatText("carousel.item.prev"),
                }}
              >
                {destinations.map((destination, index) => (
                  <SimilarDestinationCard
                    key={destinationKeyHelper.next()}
                    destination={destination}
                    destinationImageUrl={optimizedImageUrl(destination.media?.mediaUrl)}
                    cardIndex={index}
                  />
                ))}
              </IsomorphicCarousel>
            </UitkSpacing>
          </div>
        </UitkSpacing>
      </ViewSmall>

      <ViewLarge>
        <UitkSpacing margin={{ blockend: "six" }}>
          <div id={id} className="SimilarDestinations" data-fm={fmId} data-fm-title-id={fmTitleId}>
            <UitkHeading size={4} tag="h3">
              {localizedTitle}
            </UitkHeading>

            <UitkSpacing margin={{ blockstart: "three" }}>
              <UitkLayoutGrid columns={4} space="three">
                {destinations.map((destination, index) => (
                  <UitkLayoutGridItem key={destinationKeyHelper.next()}>
                    <SimilarDestinationCard
                      destination={destination}
                      destinationImageUrl={optimizedImageUrl(destination.media?.mediaUrl)}
                      cardIndex={index}
                    />
                  </UitkLayoutGridItem>
                ))}
              </UitkLayoutGrid>
            </UitkSpacing>
          </div>
        </UitkSpacing>
      </ViewLarge>
    </Viewport>
  );
});

export default SimilarDestinations;
